import React, { useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  FormErrorMessage,
  VStack,
} from '@chakra-ui/react'
import apiService from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface AddUserModalProps {
  isOpen: boolean
  onClose: () => void
  organizationId: number
  onSuccess: () => void
}

export default function AddUserModal({
  isOpen,
  onClose,
  organizationId,
  onSuccess,
}: AddUserModalProps) {
  const toast = useToast()
  const { t } = useAppTranslation('admin')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'viewer' as 'admin' | 'editor' | 'viewer',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value })
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' })
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = t('addUser.validation.nameRequired')
    }

    if (!formData.email.trim()) {
      newErrors.email = t('addUser.validation.emailRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('addUser.validation.invalidEmail')
    }

    if (!formData.password) {
      newErrors.password = t('addUser.validation.passwordRequired')
    } else if (formData.password.length < 8) {
      newErrors.password = t('addUser.validation.passwordMin')
    }

    if (!formData.role) {
      newErrors.role = t('addUser.validation.roleRequired')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)
      await apiService.addUserToOrganization(organizationId, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
      })
      toast({
        title: t('addUser.toast.success'),
        description: t('addUser.toast.successDescription'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      })
      onSuccess()
      handleClose()
    } catch (error: any) {
      const serverErrors = error.response?.data?.errors
      if (serverErrors) {
        const mapped: Record<string, string> = {}
        Object.entries(serverErrors).forEach(([key, msgs]) => {
          mapped[key] = Array.isArray(msgs) ? (msgs as string[])[0] : String(msgs)
        })
        setErrors(mapped)
      } else {
        toast({
          title: t('addUser.toast.error'),
          description: error.response?.data?.error || t('addUser.toast.errorDescription'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({ name: '', email: '', password: '', role: 'viewer' })
    setErrors({})
    setLoading(false)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('addUser.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired isInvalid={!!errors.name}>
              <FormLabel>{t('addUser.name')}</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t('addUser.namePlaceholder')}
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.email}>
              <FormLabel>{t('addUser.email')}</FormLabel>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder={t('addUser.emailPlaceholder')}
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.password}>
              <FormLabel>{t('addUser.password')}</FormLabel>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder={t('addUser.passwordPlaceholder')}
              />
              <FormErrorMessage>{errors.password}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.role}>
              <FormLabel>{t('addUser.role')}</FormLabel>
              <Select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
              >
                <option value="viewer">{t('inviteUser.roleViewer')}</option>
                <option value="editor">{t('inviteUser.roleEditor')}</option>
                <option value="admin">{t('inviteUser.roleAdmin')}</option>
              </Select>
              <FormErrorMessage>{errors.role}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            {t('addUser.cancel')}
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText={t('addUser.submitting')}
          >
            {t('addUser.submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
